import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { deleteTranscript, listTranscripts } from "../app/transcripts";
import type { TranscriptSummary } from "../app/ui-types";
import { displayTranscriptTitle } from "../app/transcript-title";
import type { ActiveCaptureMetadata } from "../app/storage";

type LibraryFilter = "open" | "finalised" | "all";

type TranscriptLibraryProps = {
  refreshKey?: number;
  pendingLocalRecovery?: ActiveCaptureMetadata | null;
  onNew: () => void;
  onOpen: (transcript: TranscriptSummary) => void;
  onDeleted?: (transcript: TranscriptSummary) => void;
  onOpenLocalRecovery?: (matterId: string) => void;
  onOpenDeviceRecovery?: () => void;
  onDiscardLocalRecovery?: () => void;
};

const relativeDate = (iso: string) => {
  const value = Date.parse(iso);
  if (!Number.isFinite(value)) return "—";
  const days = Math.floor((Date.now() - value) / 86_400_000);
  if (days <= 0) {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit"
    }).format(value);
  }
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: new Date(value).getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  }).format(value);
};

const duration = (milliseconds: number) => {
  if (milliseconds <= 0) return "0 min";
  const minutes = Math.max(1, Math.round(milliseconds / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
};

const actionLabel = (transcript: TranscriptSummary) => {
  if (transcript.attention === "recovery-needed") return "Recover";
  if (transcript.attention === "recording" || transcript.attention === "processing") {
    return "Open";
  }
  if (transcript.status === "finalised") return "View transcript";
  if (transcript.sessionCount === 0) return "Start recording";
  return "Continue transcript";
};

function TranscriptRow({
  transcript,
  onOpen,
  onDelete,
  deleting
}: {
  transcript: TranscriptSummary;
  onOpen: (transcript: TranscriptSummary) => void;
  onDelete: (transcript: TranscriptSummary) => void;
  deleting: boolean;
}) {
  const title = displayTranscriptTitle(transcript.title, transcript.createdAt);
  return (
    <article className="transcript-index-row">
      <button
        className="transcript-index-row__main"
        type="button"
        onClick={() => onOpen(transcript)}
        aria-label={`${actionLabel(transcript)}: ${title}`}
      >
        <span className="transcript-index-row__identity">
          <strong>{title}</strong>
          <span>{transcript.reference || "No reference"}</span>
        </span>
        <span className={`transcript-status transcript-status--${transcript.status}`}>
          {transcript.attention === "recovery-needed"
            ? "Recovery needed"
            : transcript.attention === "processing"
              ? "Processing"
              : transcript.attention === "recording"
                ? "Active session"
                : transcript.status === "finalised"
                  ? "Finalised"
                  : transcript.sessionCount === 0
                    ? "Draft"
                    : "Open"}
        </span>
        <span className="transcript-index-row__metric">
          <small>Activity</small>
          {relativeDate(transcript.updatedAt)}
        </span>
        <span className="transcript-index-row__metric">
          <small>Sessions</small>
          {transcript.sessionCount}
        </span>
        <span className="transcript-index-row__metric">
          <small>Duration</small>
          {duration(transcript.durationMs)}
        </span>
        <span className="transcript-index-row__action">
          {actionLabel(transcript)}
          {transcript.attention === "recovery-needed" ? (
            <RotateCcw size={15} aria-hidden="true" />
          ) : (
            <ArrowUpRight size={15} aria-hidden="true" />
          )}
        </span>
      </button>
      <button
        className="transcript-index-row__delete"
        type="button"
        onClick={() => onDelete(transcript)}
        disabled={
          deleting ||
          transcript.attention === "recording" ||
          transcript.attention === "processing"
        }
        aria-label={`Delete transcript: ${title}`}
        title={
          transcript.attention === "recording" || transcript.attention === "processing"
            ? "Stop the active session before deleting this transcript"
            : "Delete transcript"
        }
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </article>
  );
}

export function TranscriptLibrary({
  refreshKey = 0,
  pendingLocalRecovery = null,
  onNew,
  onOpen,
  onDeleted,
  onOpenLocalRecovery,
  onOpenDeviceRecovery,
  onDiscardLocalRecovery
}: TranscriptLibraryProps) {
  const [filter, setFilter] = useState<LibraryFilter>("open");
  const [query, setQuery] = useState("");
  const [transcripts, setTranscripts] = useState<TranscriptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTranscripts(await listTranscripts(filter, query));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Transcripts could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [filter, query]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [load, refreshKey, query]);

  const recoveryItems = useMemo(
    () => transcripts.filter((transcript) => transcript.attention === "recovery-needed"),
    [transcripts]
  );
  const regularItems = useMemo(
    () => transcripts.filter((transcript) => transcript.attention !== "recovery-needed"),
    [transcripts]
  );

  const remove = useCallback(async (transcript: TranscriptSummary) => {
    const title = displayTranscriptTitle(transcript.title, transcript.createdAt);
    if (
      !window.confirm(
        `Delete “${title}”?\n\nThis permanently deletes its sessions, audio, exports and sharing links. This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingId(transcript.id);
    setError(null);
    try {
      await deleteTranscript(transcript.id);
      setTranscripts((current) => current.filter((item) => item.id !== transcript.id));
      onDeleted?.(transcript);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Transcript could not be deleted.");
    } finally {
      setDeletingId(null);
    }
  }, [onDeleted]);

  return (
    <section className="transcript-library" aria-labelledby="transcripts-title">
      <header className="transcript-library__head">
        <div>
          <h1 id="transcripts-title">Transcripts</h1>
          <p>Every session stays with its transcript until you choose to finalise it.</p>
        </div>
        <button
          className="button button--primary transcript-library__new"
          type="button"
          onClick={onNew}
        >
          <Plus size={16} aria-hidden="true" />
          New transcript
        </button>
      </header>

      {pendingLocalRecovery && (
        <div className="transcript-library__blocker" role="status">
          <span>An unfinished capture is preserved on this device.</span>
          <span className="transcript-library__blocker-actions">
            {pendingLocalRecovery.matterId && onOpenLocalRecovery ? (
              <button
                className="button button--text"
                type="button"
                onClick={() => onOpenLocalRecovery(pendingLocalRecovery.matterId!)}
              >
                Open unfinished capture
              </button>
            ) : (
              <button
                className="button button--text"
                type="button"
                onClick={onOpenDeviceRecovery}
                disabled={!onOpenDeviceRecovery}
              >
                Open device recovery
              </button>
            )}
            <button
              className="button button--text"
              type="button"
              onClick={onDiscardLocalRecovery}
              disabled={!onDiscardLocalRecovery}
            >
              Discard unfinished capture
            </button>
          </span>
        </div>
      )}

      <div className="transcript-library__tools">
        <label className="transcript-search">
          <Search size={16} aria-hidden="true" />
          <span className="sr-only">Search transcripts</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search title, reference or participant"
          />
        </label>
        <div className="transcript-filters" aria-label="Filter transcripts">
          {(["open", "finalised", "all"] as const).map((value) => (
            <button
              type="button"
              key={value}
              className={filter === value ? "is-active" : ""}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === "open" ? "Open" : value === "finalised" ? "Finalised" : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="transcript-index" aria-live="polite" aria-busy={loading}>
        {recoveryItems.length > 0 && (
          <section className="transcript-recovery" aria-labelledby="recovery-title">
            <header>
              <h2 id="recovery-title">Needs attention</h2>
              <p>Captured evidence is preserved. Open the transcript to finish recovery.</p>
            </header>
            {recoveryItems.map((transcript) => (
              <TranscriptRow
                key={transcript.id}
                transcript={transcript}
                onOpen={onOpen}
                onDelete={(item) => void remove(item)}
                deleting={deletingId === transcript.id}
              />
            ))}
          </section>
        )}
        {loading && transcripts.length === 0 ? (
          <p className="transcript-index__empty">Loading transcripts…</p>
        ) : error ? (
          <div className="transcript-index__empty" role="alert">
            <p>{error}</p>
            <button className="button button--text" type="button" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : regularItems.length === 0 && recoveryItems.length === 0 ? (
          <div className="transcript-index__empty">
            <p>{query ? "No transcripts match this search." : "No transcripts here yet."}</p>
            {!query && filter !== "finalised" && (
              <button
                className="button button--text"
                type="button"
                onClick={onNew}
              >
                Create the first transcript
              </button>
            )}
          </div>
        ) : (
          regularItems.map((transcript) => (
            <TranscriptRow
              key={transcript.id}
              transcript={transcript}
              onOpen={onOpen}
              onDelete={(item) => void remove(item)}
              deleting={deletingId === transcript.id}
            />
          ))
        )}
      </div>
    </section>
  );
}
